import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
   COMMIT_FILTER_OPTIONS,
   QUEUE_SORTABLE_COLUMNS,
   STATUS_SORTABLE_KEYS,
   STATUS_TABS,
   VIEW_TABS,
} from '../constants';

// ---------------------------------------------------------------------------
// Filtros/visão/caso aberto — URL como fonte da verdade (navegacao.md §1.1),
// com o desvio consciente ratificado para telas de trabalho (mesmo do dashboard
// 0084): isto é uma FILA DE TRABALHO, não uma tela de pesquisa — ela carrega no
// mount com o default (`status=OPEN`) e reage na hora ao filtro. O que se
// preserva da regra: URL espelha o recorte com `replace: true` (sem empilhar
// histórico), F5/deep-link restauram o contexto exato — inclusive o caso
// aberto no drawer.
//
// Leitura: UMA vez, no mount. Escrita: a cada mudança, só com os valores
// diferentes do default (URL limpa no recorte padrão).
//
// Carregamento SOB DEMANDA (decisão 2026-08-14): os controles editam um
// RASCUNHO no componente; aqui só chega o recorte confirmado pelo botão
// Pesquisar — `applyFilters` grava tudo de uma vez, o que dispara UM fetch
// (rascunho × aplicado da navegacao.md §1.3, adaptado à fila de trabalho).
// ---------------------------------------------------------------------------

const DEFAULT_STATUS = 'OPEN';
const DEFAULT_VIEW = 'kanban';

const STATUS_VALUES = STATUS_TABS.map((tab) => tab.value);
const VIEW_VALUES = VIEW_TABS.map((tab) => tab.value);
const COMMIT_VALUES = COMMIT_FILTER_OPTIONS.map((option) => option.value);

function readInitialState(searchParams) {
   const rawStatus = searchParams.get('status');
   const rawView = searchParams.get('view');
   const rawAssignee = searchParams.get('assignee');
   const rawCommit = searchParams.get('commit');
   const rawOrderBy = searchParams.get('order_by');
   const rawOrderDir = searchParams.get('order_dir');
   const rawCase = Number(searchParams.get('case'));

   // `assignee`: 'none' (sem responsável) ou id numérico. Lixo na URL vira
   // "todos" em vez de request inválido.
   let assignee = 'all';
   if (rawAssignee === 'none') assignee = 'none';
   else if (Number.isInteger(Number(rawAssignee)) && Number(rawAssignee) > 0) {
      assignee = rawAssignee;
   }

   const status = STATUS_VALUES.includes(rawStatus) ? rawStatus : DEFAULT_STATUS;

   // Ordenação da Fila (handoff 0091): só entra se estiver na whitelist do
   // backend — lixo na URL vira ordem default em vez de 422 no primeiro
   // request. Desde 0102 também precisa fazer sentido na ABA: um deep-link com
   // `status=OPEN&order_by=settled_amount` ordenaria por uma coluna que a aba
   // nem renderiza (o cabeçalho não teria como exibir o estado do sort).
   const sortIsUsable =
      QUEUE_SORTABLE_COLUMNS[rawOrderBy] &&
      (STATUS_SORTABLE_KEYS[status] ?? STATUS_SORTABLE_KEYS.OPEN).has(
         rawOrderBy
      );

   return {
      search: searchParams.get('search') ?? '',
      status,
      assignee,
      commit: COMMIT_VALUES.includes(rawCommit) ? rawCommit : 'all',
      onlyPending: searchParams.get('pending') === '1',
      sort: sortIsUsable
         ? {
              key: rawOrderBy,
              direction: rawOrderDir === 'desc' ? 'desc' : 'asc',
           }
         : { key: null, direction: null },
      view: VIEW_VALUES.includes(rawView) ? rawView : DEFAULT_VIEW,
      // Exibição das raias vazias no kanban (decisão 2026-08-17): desmarcado
      // por default — raia sem caso (stage_counts) fica oculta até o operador
      // ligar o toggle. É estado de EXIBIÇÃO, não filtro de dados.
      showEmpty: searchParams.get('show_empty') === '1',
      openCaseId: Number.isInteger(rawCase) && rawCase > 0 ? rawCase : null,
   };
}

export default function useDunningFilters() {
   const [searchParams, setSearchParams] = useSearchParams();

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const [state, setState] = useState(() => readInitialState(searchParams));

   const currentQueryRef = useRef(searchParams.toString());
   currentQueryRef.current = searchParams.toString();

   useEffect(() => {
      const params = {};
      if (state.search) params.search = state.search;
      if (state.status !== DEFAULT_STATUS) params.status = state.status;
      if (state.assignee !== 'all') params.assignee = state.assignee;
      if (state.commit !== 'all') params.commit = state.commit;
      if (state.onlyPending) params.pending = '1';
      // Só vai pra URL quando difere do default (navegacao.md §1.9).
      if (state.sort.key) {
         params.order_by = state.sort.key;
         if (state.sort.direction === 'desc') params.order_dir = 'desc';
      }
      if (state.view !== DEFAULT_VIEW) params.view = state.view;
      if (state.showEmpty) params.show_empty = '1';
      if (state.openCaseId) params.case = String(state.openCaseId);

      const next = new URLSearchParams(params).toString();
      if (next === currentQueryRef.current) return;

      setSearchParams(params, { replace: true });
   }, [state, setSearchParams]);

   // Recorte que vai ao BACKEND (o resto é estado de leitura da tela).
   // `assignee === 'none'` não tem filtro no contrato — vira pós-filtro no
   // cliente (useDunningList), então não entra aqui.
   const listFilters = useMemo(
      () => ({
         search: state.search,
         status: state.status,
         assignedUserId:
            state.assignee !== 'all' && state.assignee !== 'none'
               ? Number(state.assignee)
               : undefined,
         actionStatus: state.onlyPending ? 'PENDING' : undefined,
         orderBy: state.sort.key ?? undefined,
         orderDir: state.sort.key ? state.sort.direction : undefined,
      }),
      [
         state.search,
         state.status,
         state.assignee,
         state.onlyPending,
         state.sort,
      ]
   );

   // Aplica o recorte inteiro num setState só → um único refetch downstream.
   // Nova busca ZERA a ordenação para o default de urgência (navegacao.md
   // §1.9: "nova busca por filtros zera a ordenação").
   const applyFilters = useCallback(
      ({ search, status, assignee, commit, onlyPending }) => {
         setState((prev) => ({
            ...prev,
            search: search.trim(),
            status,
            assignee,
            commit,
            onlyPending,
            sort: { key: null, direction: null },
         }));
      },
      []
   );

   // Clique de ordenação é ação EXPLÍCITA → aplica na hora, sem passar pelo
   // rascunho/Pesquisar (não fere o carregamento sob demanda — §1.2 proíbe
   // auto-query ao digitar, não o clique do usuário).
   const setSort = useCallback((sort) => {
      setState((prev) => ({ ...prev, sort }));
   }, []);

   const setView = useCallback((view) => {
      setState((prev) => ({ ...prev, view }));
   }, []);

   // Toggle de exibição — aplica na hora, sem passar pelo rascunho/Pesquisar
   // (não dispara request; só muda o que o kanban renderiza).
   const toggleShowEmpty = useCallback(() => {
      setState((prev) => ({ ...prev, showEmpty: !prev.showEmpty }));
   }, []);

   const openCase = useCallback((openCaseId) => {
      setState((prev) => ({ ...prev, openCaseId }));
   }, []);

   const closeCase = useCallback(() => {
      setState((prev) => ({ ...prev, openCaseId: null }));
   }, []);

   return {
      search: state.search,
      status: state.status,
      assignee: state.assignee,
      commit: state.commit,
      onlyPending: state.onlyPending,
      sort: state.sort,
      view: state.view,
      showEmpty: state.showEmpty,
      openCaseId: state.openCaseId,
      listFilters,
      applyFilters,
      setSort,
      setView,
      toggleShowEmpty,
      openCase,
      closeCase,
   };
}
